import { Component } from '@angular/core';
import { OnInit, OnDestroy, HostListener, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { DomSanitizer } from '@angular/platform-browser';
import { Subscription } from 'rxjs';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ConfigService } from '../../services/config.service';
import { AlertsService } from '../../services/alerts.service';
import { UserService } from '../../services/user.service';
import { RoutingService } from '../../services/routing.service';
import { SearchService } from '../../search/services/search.service';
import { MapService } from '../../map/services/map.service';
import { GeoService } from '../../map/services/geo.service';
import { SaveLocationComponent } from '../save-location/save-location.component';
import { formatString } from '../../utils/string-utils';

import { BaseMap, MapControl, LengthUnit, ArealUnit, ProjectionUnit, SearchPredicate } from '../../models/map.interface';

import { loadModules } from 'esri-loader';
import cloneDeep from 'lodash/cloneDeep';

declare let Terraformer: any; // terraformer-wkt-parser.js, https://github.com/esri/terraformer-wkt-parser
declare let proj4: any;       // proj4js-2.3.14.js, http://proj4js.org/


/**
 * Component providing a map view of geographic search results.
 */
@Component({
	selector: 'map-view',
	templateUrl: './map-view.component.html',
	styleUrl: './map-view.component.css'
})
export class MapViewComponent implements OnInit, OnDestroy {

	private userEventSubscription!: Subscription;
	private searchEventSubscription!: Subscription;
	private routingEventSubscription!: Subscription;
	private mapEventSubscription!: Subscription;

	LOCALIZATION: any;
	userPermissions: { [key: string]: any } = {};

	mapId: string = "mainMap";
	mapView: any;
	mapReady: boolean = false;
	basemapConfigs: BaseMap[] = [];
	searchWidget: any;
	sketchViewModel: any;

	mapControl!: MapControl;
	lengthUnits: LengthUnit[] = [];
	arealUnits: ArealUnit[] = [];
	projectionUnits: ProjectionUnit[] = [];
	geometrySearchPredicates: SearchPredicate[] = [];
	tools = {
		activeShape: '',
		hasAOI: false
	};
	mode: string = 'pan'; // pan, zoomin, zoomout, distance, area
	hide: any = {
		hideTools: false,
		hideExtent: false,
		hideDetach: false
	};

	viewCoords: boolean = false;
	coordinates: string | null = null;

	records: any[] = [];
	filmstripRecords: any[] = [];

	viewType?: string; // Corresponds to $stateParams.v
	mapDetached: boolean = false;
	trackExtent: boolean = true;
	previousExtent: any | null = null;
	debounceTimeout: any = null;
	previewOpen: boolean = false;

	// Projection layers
	treeData: any[] = [];
	inputLayers: any[] = [];

	// TODO
	chippingEnabled: boolean = false;
	chipOn: boolean = false;
	supportedPackageTypes: any[] = [];
	chip = {
		x1: 0,
		x2: 0,
		y1: 0,
		y2: 0,
		wkt: "",
		chipFormat: "",
		//	    chipPackageType: "PRODUCT",
		//	    chipRequestName: "Image Chip",
		//	    chipEmailAddress: this.user.email,
		//	    chipEmailNotification: false,
		//	    searchResults: [],
		//	    selectedResults: {},
		//	    selectedResultsArr: [],
		//	    searching: false,
		//	    searchTotal: 0,
		//	    pageSize: 10,
		//	    pageIndex: 1
	};

	constructor(
		private ngbModal: NgbModal,
		private zone: NgZone,
		private router: Router,
		private sanitizer: DomSanitizer,
		private userService: UserService,
		private configService: ConfigService,
		private alertsService: AlertsService,
		private searchService: SearchService,
		private mapService: MapService,
		private geoService: GeoService,
		private routingService: RoutingService
	) {
		this.LOCALIZATION = this.userService.LOCALIZATION;
		this.userPermissions = this.userService.getUserPermissionsMap();
	}

	ngOnInit(): void {
		this.basemapConfigs = this.configService.getConfig()?.esri?.basemaps || [];
		this.mapControl = this.mapService.getMapControl();
		this.lengthUnits = this.mapService.getLengthUnits();
		this.arealUnits = this.mapService.getArealUnits();
		this.projectionUnits = this.mapService.getProjectionUnits();
		this.geometrySearchPredicates = this.mapService.getSearchPredicates();
		this.chippingEnabled = this.configService.getConfig().esri.chippingEnabled;
		this.viewType = this.routingService.getQueryParam('v');
		this.inputLayers = this.configService.getConfig().esri.layers || []; // Only display current domain layers

		this.userEventSubscription = this.userService.broadcastEvent.subscribe((event: string) => {
			if ('loggedin' === event) {
				this.onRedrawLocations();
				this.processSearchResults();
			}
			else if ('localizationLoaded' === event) {
				this.LOCALIZATION = this.userService.LOCALIZATION;
			} else if ("userPrefsUpdate" === event) {
				this.userPermissions = this.userService.getUserPermissionsMap();
				this.processLayers();
			}
		});

		this.searchEventSubscription = this.searchService.broadcastEvent.subscribe((event: any) => {
			if ('searchEnded' === event.type) {
				this.processSearchResults();
			}
			else if ('clearSearch' === event.type) {
				this.records = [];
			}
			else if ('cleanSearches' === event.type) {
				if (this.mapReady) {
					this.cleanMap();
				}
				this.updateFilmstrip([]);
			}
			else if ('map:FullExtent' === event.type) {
				if (this.mapReady) {
					this.fullExtent();
				}
			}
		});

		this.mapEventSubscription = this.mapService.mapServiceEvent.subscribe((event: any) => {
			if ('updateAOI' === event.eventType) {
				if (this.chipOn) {
					this.searchChipProducts(1, undefined, event.data.aoi);
				}
			}
			else if ('RedrawMapAOI' === event.eventType) {
				this.wktToAoi(event.data.aoi, event.data.forceZoom);
			}
			else if ('Zoom&SelectRecord' === event.eventType) {
				this.onZoomSelectRecord(event.data.geom, event.data.uuid, event.data.update);
			}
			else if ('ZoomIdentify' === event.eventType) {
				this.onZoomIdentify(event.data.geometry);
			}
			else if ('Show&ZoomIdentify' === event.eventType) {
				this.onShowZoomIdentify(event.data);
			}
			else if ('AddPointLocate&Zoom' === event.eventType) {
				this.onAddPointLocateZoom(event.data);
			}
			else if ('AddLocation&Zoom' === event.eventType) {
				this.onAddLocationZoom(event.data.geometry, event.data.search, event.data.identify);
			}
			else if ('selectedGeomChanged' === event.eventType) {
				this.onSelectedGeomChanged(event.data.geometry, event.data.record, event.data.forceZoom);
			}
			else if ('redrawLocations' === event.eventType) {
				this.onRedrawLocations();
			}
			else if ('clearAOI' === event.eventType) {
				this.clearAOI();
			}
			else if ('AddNewAOI' === event.eventType) {
				this.addAOI(event.data.geometry, { forceDraw: event.data.forceDraw, forceZoom: event.data.forceZoom, buffer: event.data.buffer });
			}
			else if ('trackExtentChanges' === event.eventType) {
				this.trackExtent = event.data.trackExtent;
			}
			else if ('result:ZoomToRecord' === event.eventType) {
				this.onZoomTo(event.data.record);
			}
			else if ('result:UpdateMapRecordPreview' === event.eventType) {
				this.onUpdateMapRecordPreview(event.data.record);
			}
			else if ('result:UpdateMapRecordActiveLocation' === event.eventType) {
				this.onUpdateMapRecordActiveLocation(event.data.record, event.data.trigger);
			}
			else if ('unselectRecordMap' === event.eventType) {
				this.unselectRecordMap();
			}
			else if ('expandRecord' === event.eventType) {
				this.onExpandRecord(event.data.record);
			}
		});

		// Listen for route changes, specifically related to the view type

		this.routingEventSubscription = this.routingService.routingEvent.subscribe(event => {
			if (event.eventType === 'navigationEnded') {
				this.processSearchResults();
			}
		});

		this.initializeMap();
	}

	ngOnDestroy() {
		if (this.mapView) {
			this.mapView.destroy();
		}

		if (this.userEventSubscription) {
			this.userEventSubscription.unsubscribe();
		}

		if (this.routingEventSubscription) {
			this.routingEventSubscription.unsubscribe();
		}

		if (this.searchEventSubscription) {
			this.searchEventSubscription.unsubscribe();
		}

		if (this.mapEventSubscription) {
			this.mapEventSubscription.unsubscribe();
		}
	}

	/**
	 * Process the user WFS-service search results.
	 */
	private processSearchResults(): void {
		this.viewType = this.routingService.getQueryParam('v');
		if (!this.viewType || this.viewType !== 'M') {
			return;
		}

		console.log('Processing search results!');

		let results = this.searchService.results;

		if (!results || results.error) {
			this.records = [];
			return;
		}

		if (!this.mapReady) {
			console.log("Map is not ready.");
			return;
		}

		if (this.mapReady && this.configService.getConfig()?.esri?.enabled) {
			this.getMapLayer("search").then((layer: any) => {
				layer.removeAll();
			});

			this.getMapLayer("preview").then((layer: any) => {
				layer.removeAll();
			});

			this.getMapLayer("multigeometry").then((layer: any) => {
				layer.removeAll();
			});

			this.getMapLayer("projection").then((layer: any) => {
				layer.source.elements.removeAll();
			});

			this.mapView.closePopup();
			this.unselectRecordMap();
			this.updateWFSLayer();
		}
	}

	private async initializeMap(): Promise<any> {
		this.mapView = await this.loadMap();
		this.mapService.loadBaseMap(this.mapView);

		this.mapView.when(() => {
			console.log("%s view is ready. All layers loaded.", this.mapId);

			let coords = this.configService.getConfig().esri.center ? this.configService.getConfig().esri.center.split(",") : ["0", "0"];
			this.mapService.centerAndZoomMap(this.mapView, coords, this.configService.getConfig().esri.zoom);

			this.onMapLoaded();

			this.mapReady = true;
			this.mapService.broadcastMapReadyEvent({ ready: this.mapReady });

			if (this.searchService.results) {
				this.updateWFSLayer();
			}
		});
	}

	private async loadMap(): Promise<any> {
		try {
			const [Map, MapView] = await loadModules(['esri/Map', 'esri/views/MapView']);
			const map = new Map({
				basemap: 'topo-vector'
			});

			const view = new MapView({
				container: 'mapViewDiv',
				map: map,
				center: [-118, 34],
				zoom: 10
			});

			return view;
		} catch (err) {
			console.error("ArcGIS API load error: ", err)
			return undefined;
		}
	}

	private async onMapLoaded(): Promise<any> {
		const [SketchViewModel, Search] = await loadModules(['esri/widgets/Sketch/SketchViewModel', 'esri/widgets/Search']);

		this.mapView.on("pointer-move", (evt: any) => this.showCoordinates(evt));
		this.mapView.on("pointer-leave", (evt: any) => this.removeCoordinates());
		this.mapView.on("drag", (evt: any) => this.showCoordinates(evt));
		this.mapView.on("immediate-click", (evt: any) => this.displayInfo(evt));

		this.setupExtentWatcher();

		this.processLayers();

		this.searchWidget = new Search({
			view: this.mapView,
			container: "mapLocationBar"
		});
		this.searchWidget.on("search-complete", (event: any) => {
			// this.searchWidgetResults = event.results[0].results;
			console.log(event.results[0].results);
		});
		this.searchWidget.on("select-result", (event: any) => {
			let geojson = Terraformer.ArcGIS.parse(event.result.feature.geometry);
			let wkt = Terraformer.WKT.convert(geojson);

			// Build a custom feature display. Can probably replace this with a template.
			let content =
				`<div class="map-searchwidget-content">` +
				`<div class="searchwidget-address">${event.result.feature.attributes.Match_addr}</div>` +
				`<div class="searchwidget-address-type">${event.result.feature.attributes.Addr_type} in ${event.result.feature.attributes.City}</div>` +
				`</div>`;

			this.mapView.goTo({
				target: event.result.feature.geometry,
				zoom: 15
			}).then(() => {
				// The popup displays docked by default in narrow viewports
				this.mapView.popup.open({
					title: event.result.name,
					content: content,
					location: event.result.feature.geometry,
					shouldFocus: true
				});
			});

			//			// Add a location point graphic to the map
			//			let graphic = new Graphic({
			//				geometry: event.result.feature.geometry,
			//				symbol: this.mapService.getSymbol('locate-point'),
			//				attributes: {
			//					"wkt": wkt
			//				}
			//			});
			//
			//			this.mapLayer.add(graphic);
		});


		let utilMapLayer = await this.getMapLayer("utility");
		this.sketchViewModel = new SketchViewModel({
			view: this.mapView,
			layer: utilMapLayer,
			showTooltips: false,
			pointSymbol: {
				type: "simple-marker",
				style: "square",
				color: "red",
				size: "8px"
			},
			polylineSymbol: {
				type: "simple-line",
				color: "green",
				width: "2px"
			},
			polygonSymbol: {
				type: "simple-fill",
				color: [255, 255, 255, 0.5],
				outline: {
					color: "blue",
					width: 1
				}
			}
		});
		this.sketchViewModel.on("create", (event: any) => {
			if (event.state === "complete") {
				// Handle the completion of the drawing
				console.log("Drawing completed:", event.graphic);
				this.addGraphic(event.graphic.geometry, { forceDraw: event.forceDraw, forceZoom: event.forceZoom, buffer: event.buffer });
			}
		});
	}

	/**
	 * Enables users to select points on the map and display an info popup.
	 */
	private async displayInfo(evt: any): Promise<void> {
		if (this.mode !== "pan") { return; }

		const [Point] = await loadModules(['esri/geometry/Point']);
		this.mapView.hitTest(evt).then(
			(response: any) => {
				let results = response.results;

				results.forEach((result: any) => {
					if (result.graphic && result.graphic.layer && 'wfs' === result.graphic.layer.id) {
						// When clicking on a cluster point, both the text portion (that displays the cluster number)
						// and the geometry portion containing the search result attributes are collected by the hitTest. 
						if (!result.graphic.attributes.isText) {
							let mapPoint = new Point({
								x: result.graphic.geometry.x,
								y: result.graphic.geometry.y
							});

							this.mapService.pointToExtent(this.mapView, mapPoint, 10).then(
								(extent: any) => {
									this.processWFSGraphics(extent);
								}
							);
						}
					}
				});
			}
		);
	}

	/**
	 *  Update the drawn points when moving around the map, 
	 *  but only when the extent has changed above a certain threshold.
	 */
	private async setupExtentWatcher(): Promise<void> {
		const [Extent] = await loadModules(['esri/geometry/Extent']);

		this.mapView.watch('extent', (newExtent: any) => {
			clearTimeout(this.debounceTimeout);

			this.debounceTimeout = setTimeout(() => {
				console.log('Extent changed:', newExtent);
				this.onExtentChanged(newExtent);
				this.previousExtent = newExtent.clone();
			}, 250);
		});
	}

	private onExtentChanged(extent: any): void {
		if (this.previousExtent && this.trackExtent) {
			const threshold = 9; // NOTE: This is an ARBITRARY number for the time being
			const distance = this.mapService.getExtentsDistance(extent, this.previousExtent);
			console.log('Distance between extents: %s units', distance);

			if (distance >= threshold) {
				this.updateWFSLayer();
			}
		}
	}

	/**
	 * Shows the current mouse coordinates on the map.
	 */
	private async showCoordinates(evt: any): Promise<void> {
		const [webMercatorUtils] = await loadModules(['esri/geometry/support/webMercatorUtils']);

		// The map is in web mercator but display coordinates are in geographic (lat, long);
		let mapPoint = this.mapView.toMap({ x: evt.x, y: evt.y })
		let mp = webMercatorUtils.webMercatorToGeographic(mapPoint);

		// Not sure this is actually working or called in normal usage
		//		const proj = this.projectionUnits.find((e: any) =>
		//			e.value === this.mapControl.projectionUnit
		//		);
		//		if (proj) {
		//			// convert the point mp from its current coordinate system (usually WGS84) into the target projection
		//			mp = proj4(proj, mp);
		//		}

		this.zone.run(() => {
			this.coordinates = mp.x.toFixed(3) + ", " + mp.y.toFixed(3);
		});
	}

	private removeCoordinates(): void {
		this.zone.run(() => {
			this.coordinates = null;
		});
	}

	cleanMap(): void {
		// NOTE: map may not reflect graphics change when open in debugger
		this.mapView.map.layers.forEach((layer: any) => {
			console.log("Removing %s layer items for layer %s", layer.type, layer.id);

			if (layer.type === 'graphics') {
				layer.removeAll();
				layer.emit("graphics-update");
			} else if (layer.type === 'geojson' || layer.type === 'feature') {
				this.mapView.map.remove(layer);
			} else if (layer.type === 'media') {
				layer.source.elements.removeAll();
			}
		});
		this.mapView.graphics.removeAll();
		this.mapView.ui.empty();
		this.mapView.closePopup();
		this.unselectRecordMap();

		this.mapView.goTo(this.mapView.center, { animate: false }); // Jiggle the map to initiate a rerender
	}

	private async onSelectedGeomChanged(geometry: any, uuid: any, forceZoom: boolean = false) {
		this.mapView.popup.visible = false;

		this.getMapLayer("multigeometry").then((layer: any) => {
			layer.removeAll();
		});

		let previewLayer = await this.getMapLayer("preview");
		previewLayer.removeAll();

		const [Graphic] = await loadModules(['esri/Graphic']);

		if (geometry.type === 'multipoint' || geometry.type === 'point') {
			previewLayer.add(new Graphic(geometry, this.mapService.getSymbol('single')));
		} else {
			previewLayer.add(new Graphic(geometry, this.mapService.getSymbol('poly-wfs-selected')));
		}
		if (forceZoom) {
			this.onZoomSelectRecord(geometry, uuid, true);
		}
		//	    $rootScope.$broadcast("grid:flashMapRecordPriview"); // TODO need this?
	}

	private async onRedrawLocations(): Promise<void> {
		this.getMapLayer("savedLocation").then((layer: any) => {
			layer.removeAll();
		});
	}

	private async onZoomSelectRecord(geo: any, uuid: any, update: boolean = false, extent?: any): Promise<void> {
		const [Extent] = await loadModules(['esri/geometry/Extent']);

		let taxLotExtent = "";
		if (extent) {
			taxLotExtent = new Extent(extent);
		} else {
			taxLotExtent = geo.extent;
		}
		if (taxLotExtent) {
			this.mapView.goTo({
				target: taxLotExtent
			});
		} else {
			this.mapService.centerAndZoomMap(this.mapView, [geo.x, geo.y], 15);
		}

		if (update) {
			this.mapService.broadcastUpdatePreviewEvent({
				uuid: uuid
			});
		}
	}

	private onZoomIdentify(geometry: any): void {
		let taxLotExtent = geometry.extent;
		if (taxLotExtent) {
			this.mapView.goTo({
				target: taxLotExtent
			});
		} else {
			// We can assume it's a point
			this.mapService.centerAndZoomMap(this.mapView, [geometry.x, geometry.y], 18);
		}
	}

	// This is used by non-default geo-location units
	private onShowZoomIdentify(data: any): void {
		console.log("TODO: Show&ZoomIdentify");
		//                    $timeout(function () {
		//                        if (graphicQueue)
		//                            $timeout.cancel(graphicQueue);
		//
		//                        let taxLotExtent = arg.geometry.extent;
		//                        let geometry = arg.geometry;
		//                        $scope.getMapLayer("location").removeAll();
		//                        $scope.getMapLayer("location").add(new Graphic(geometry, mapService.getSymbol('fill')));
		//							map.goTo({
		//								target: taxLotExtent
		//							});
		//                        graphicQueue = $timeout(function () {
		//                            $scope.getMapLayer("location").removeAll();
		//                            graphicQueue = null;
		//
		//                        }, 4000);
		//                    }, 500)
	}

	// This is used by non-default geo-location units
	private onAddPointLocateZoom(data: any): void {
		console.log("TODO: AddPointLocate&Zoom");
		//                    if (graphicQueue)
		//                        $timeout.cancel(graphicQueue);
		//
		//                    $scope.getMapLayer("location").removeAll();
		//
		//						map.navigationConstraints = {
		//							allowPan: true,
		//							allowZoom: true
		//						};
		//                    $scope.toggleMode("pan");
		//                    if (arg.point.type == 'multipoint') {
		//                        arg.point = arg.point.extent.getCenter();
		//                    }
		//                    mapService.centerAndZoom("mainMap", [arg.point.x, arg.point.y], 18);
		//
		//                    if (arg.search) {
		//                        let y = arg.point.y;
		//                        let x = arg.point.x;
		//
		//                        let lengthObject = $.grep(mapService.getLengthUnits(), function (n, i) {
		//                            if (n.value == configService.getConfig().lengthUnit) {
		//                                return n.name;
		//                            };
		//                        })[0];
		//
		//                        let d = configService.getConfig().searchBuffer * lengthObject.toMeter;
		//                        //rabrun;
		//                        if (arg.buffer)
		//                            d = arg.buffer;
		//
		//                        let point = new esri.geometry.Point([x * 1, y * 1]);
		//                        geom = new Circle(point, {
		//                            "radius": d,
		//                            "radiusUnit": "meters",
		//                            geodesic: true
		//                        });
		//
		//                        for (let c = 0; c < geom.rings[0].length; c++) {
		//                            geom.rings[0][c] = webMercatorUtils.lngLatToXY(geom.rings[0][c][0], geom.rings[0][c][1]);
		//                        }
		//
		//                        geom.spatialReference.wkid = 102100;
		//                        geom._srsShape = "circle";
		//                        $rootScope.$broadcast("AddNewAOI", { geometry: geom, forceZoom: true });
		//
		//                        return;
		//                    }
		//
		//                    $scope.getMapLayer("location").add(new Graphic(arg.point, mapService.getSymbol('marker')));
		//                    graphicQueue = $timeout(function () {
		//
		//                    $scope.getMapLayer("location").removeAll();
		//                    graphicQueue = null;
		//
		//                    }, 5000); 
	}

	private async onAddLocationZoom(geometry: any, search: boolean = false, identify: boolean = false): Promise<void> {
		//		if (graphicQueue) {
		//		    $timeout.cancel(graphicQueue);
		//		}

		if (search) {
			const [webMercatorUtils] = await loadModules(['esri/geometry/support/webMercatorUtils']);
			this.mapService.broadcastUpdateAOIByExtentEvent({ geometry: webMercatorUtils.geographicToWebMercator(geometry) });
			return;
		}

		const [Graphic] = await loadModules(['esri/Graphic']);
		let layer = await this.getMapLayer("location");

		if (!identify) {
			this.mapView.goTo({
				target: geometry.extent
			});
			this.sketchViewModel.cancel();
			this.mapView.navigationConstraints = {
				allowPan: true,
				allowZoom: true
			};
			this.toggleMode("pan");

			layer.add(new Graphic(geometry, this.mapService.getSymbol('fill')));
			//		    graphicQueue = $timeout(function () {
			//		        layer.removeAll();
			//		        graphicQueue = null;
			//		    }, 5000);
		} else {
			layer.add(new Graphic(geometry, this.mapService.getSymbol('fill')));
		}
	}

	private addGraphic(geometry: any, options: any) {
		console.log('Inside addGraphic(), mode = %s', this.mode);

		options = (options || {});

		// The mode will always be the default of 'pan' for AOI operations
		if (this.mode === "pan" || options.forceDraw) {
			///////////////////////////////////
			// AOI Layer (for search)
			///////////////////////////////////						
			this.addAOIGraphic(geometry, options);
		} else {
			///////////////////////////////////
			// Utility Layer (for measurement)
			///////////////////////////////////
			this.addUtilityGraphic(geometry);
		}
	}

	/**
	 * Generates a WKT string for geo-search using the WFS service.
	 */
	private async addAOIGraphic(geometry: any, options: any): Promise<any> {
		const [Graphic, Point, SpatialReference, geometryEngine, webMercatorUtils, normalizeUtils, BufferParameters, geometryService] =
			await loadModules([
				'esri/Graphic',
				'esri/geometry/Point',
				'esri/geometry/SpatialReference',
				'esri/geometry/geometryEngine',
				'esri/geometry/support/webMercatorUtils',
				'esri/geometry/support/normalizeUtils',
				'esri/rest/support/BufferParameters',
				'esri/rest/geometryService'
			]);

		this.getMapLayer("aoi").then((layer: any) => {
			layer.removeAll();
		});

		let wktString = null;

		if (this.tools.activeShape === "circle" || geometry._srsShape === "circle") {
			// Find the WKT string:
			// Extract the Circle Geometry - Get the geometry of the graphic created by the SketchViewModel
			// Calculate the Center Point and Radius
			// Radius Calculation: For a polygon, the radius is calculated by measuring the distance from the center to a point on the perimeter (e.g., the first point in the polygon's ring)
			// Construct the WKT String - Format the WKT string for the circle using the center point coordinates and the radius

			// Coordinate systems:
			// Mercator (WGS 84 / Pseudo-Mercator)
			//    The most commonly used projection in web mapping (including ArcGIS Online) is Web Mercator. The default units are in meters.
			// Geographic Coordinate System (WGS 84)
			//    If you're working with geographic coordinates (latitude and longitude), the default units are in degrees.
			//  Projected Coordinate Systems
			//    For other projected coordinate systems (like UTM or State Plane), the default units can vary.	

			// Get the center point (Web Mercator)
			let center = geometry.centroid;
			let centerPoint = center;

			// Get the first point in the geometry ring (Web Mercator) 
			// Assumes the geometry is a Polygon
			let firstPointCoords = geometry.rings[0][0];
			let firstPoint = new Point({
				x: firstPointCoords[0],
				y: firstPointCoords[1],
				spatialReference: { wkid: 102100 }
			});

			// Get the radius (using Web Mercator units)
			// Calculate the distance in the given lengthUnit (eg: meters) from center to some point on the perimeter
			let radius = geometryEngine.distance(center, firstPoint, this.mapControl.lengthUnit);

			// Get the unit of conversion for this.mapControl.lengthUnit, to xform into meters 
			//			let conversionUnit = $.grep(this.lengthUnits, function(n, i) {
			//				if (n.engineUnit == this.mapControl.lengthUnit) {
			//					return n.name;
			//				};
			//			})[0];
			let conversionUnit = this.lengthUnits.find((unit: any) => unit.engineUnit === this.mapControl.lengthUnit);

			// Get the radius in meters
			let radiusInMeters = radius / (conversionUnit?.toMeter || 1);

			// Convert the center to geographic coordinates for the WKT string
			let centerGeographic = webMercatorUtils.webMercatorToGeographic(center);

			// Create WKT string SPECIFICALLY FOR services.buildQuery: 
			// CIRCLE(center.y, center.x d=radiusInMeters)
			wktString = "CIRCLE(" + centerGeographic.y.toFixed(3) + "," + centerGeographic.x.toFixed(3) + " d=" + radiusInMeters + ")";

			console.log("Generated WKT AOI search string for length unit: %s", this.mapControl.lengthUnit);
			console.log(wktString);

			/*					
			// >>>>> TEST
			// Add a test graphic to check the validity of the WKT calculation
			// X (longitude) ranges from -180° to 180°.
			// Y (latitude) ranges from -90° to 90°.						
			let centerPointTest = new Point({
				longitude: centerGeographic.x.toFixed(3),
				latitude: centerGeographic.y.toFixed(3),
				spatialReference: { wkid: 4326 } // WGS84 spatial reference (point is in geographic coordinates)
			});
			let pointSymbolTest = this.mapService.getSymbol('single');
			pointSymbolTest.color = [0, 255, 0, 1];
			let pointGraphicTest = new Graphic({
				geometry: centerPointTest,
				symbol: pointSymbolTest
			});
			this.getMapLayer("aoi").add(pointGraphicTest);
			let circleTest = new Circle({
				center: centerPointTest,
				radius: radiusInMeters,
				radiusUnit: "meters"
			});
			let fillSymbolTest = this.mapService.getSymbol('aoi-fill');
			fillSymbolTest.color = [0, 255, 0, 0.25];
			let circleGraphicTest = new Graphic({
				geometry: circleTest,
				symbol: fillSymbolTest
			});
			this.getMapLayer("aoi").add(circleGraphicTest);
			// END TEST	<<<<<	
			*/

			this.mapService.broadcastUpdateAOIEvent({ aoi: wktString });

		} else if (this.tools.activeShape === "point") {
			let geojson = Terraformer.ArcGIS.parse(geometry);
			wktString = Terraformer.WKT.convert(geojson);

			this.mapService.broadcastUpdateAOIEvent({ aoi: wktString });

		} else if (geometry.type === 'line' || geometry.type === 'polyline') {
			let drawBuffer = (bufferedGeometries: any[]) => {
				let geom = bufferedGeometries[0];
				let geojson = Terraformer.ArcGIS.parse(geom);
				let wktString = Terraformer.WKT.convert(geojson);

				this.mapService.broadcastUpdateAOIEvent({ aoi: wktString });

				// Add the graphic to the map
				let newGraphic = new Graphic({
					geometry: geometry,
					symbol: (geometry.type === "point") ? this.mapService.getSymbol('locate-point') : this.mapService.getSymbol('line'),
					attributes: {
						"wkt": wktString
					},
					popupTemplate: null // Disable interactivity (eg. resizing, moving)
				});
				this.getMapLayer("aoi").then((layer: any) => {
					layer.add(newGraphic);
				});

				this.sketchViewModel.cancel();
				this.mapView.navigationConstraints = {
					allowPan: true,
					allowZoom: true
				};
				this.tools.activeShape = '';

				let taxLotExtent = geom.extent;
				this.mapView.goTo({
					target: taxLotExtent
				});
			};

			let params = new BufferParameters();
			params.distances = [options?.buffer?.distance];
			params.outSpatialReference = new SpatialReference({ wkid: 102100 });
			params.inSpatialReference = new SpatialReference({ wkid: 102100 });
			params.unit = geometryService[options?.buffer?.unit];
			params.unionResults = false;
			params.geodesic = false;

			const functdrawBuffer = (bufferedGeometries: any) => {
				const geom = bufferedGeometries[0];
				this.getMapLayer("aoi").then((layer: any) => {
					layer.add(geom);
				});
			};

			normalizeUtils.normalizeCentralMeridian([geometry]).then(
				(normalizedGeometries: any) => {
					let normalizedGeometry = normalizedGeometries[0];
					if (normalizedGeometry.type === "polygon") {
						// If geometry is a polygon then simplify polygon.  This will make the user drawn polygon topologically correct.
						geometryService.simplify(this.configService.getConfig().esri.geometryService, [normalizedGeometry]).then(
							(geometries: any) => {
								params.geometries = geometries;
								geometryService.buffer(params, functdrawBuffer);
							},
							(err: any) => {
								console.error("Error simplifying geometry: ", err);
							}
						);
					} else {
						params.geometries = [normalizedGeometry];
						geometryService.buffer(params, drawBuffer);
					}
				}
			);

		} else {
			// Catchall --> polygon
			let geom;
			if (geometry.spatialReference && geometry.spatialReference.wkid == 4326) {
				geom = geometry;

			} else {
				geom = webMercatorUtils.webMercatorToGeographic(geometry);
			}

			for (let i = 0; i < geom.rings.length; i++) {
				let ring = geom.rings[i];
				for (let p = 0; p < ring.length; p++) {
					let point = ring[p];
					point[0] = point[0];
					point[1] = point[1];
				}
			}

			let geojson = Terraformer.ArcGIS.parse(geom);
			wktString = Terraformer.WKT.convert(geojson);

			if (this.chipOn) {
				let ext = geom.extent;
				this.chip.x1 = ext.xmin;
				this.chip.y1 = ext.ymin;
				this.chip.x2 = ext.xmax;
				this.chip.y2 = ext.ymax;
				this.chip.wkt = wktString;
			}

			this.mapService.broadcastUpdateAOIEvent({ aoi: wktString });
		}

		// Add the graphic to the map
		let newGraphic = new Graphic({
			geometry: geometry,
			symbol: (geometry.type === "point") ? this.mapService.getSymbol('locate-point') : this.mapService.getSymbol('aoi'),
			attributes: {
				"wkt": wktString
			},
			popupTemplate: null // Disable interactivity (eg. resizing, moving)
		});

		if (this.chipOn) {
			this.getMapLayer("utility").then((layer: any) => {
				layer.add(newGraphic);
			});
		} else {
			this.getMapLayer("aoi").then((layer: any) => {
				layer.add(newGraphic);
			});
		}


		this.sketchViewModel.cancel();
		this.mapView.navigationConstraints = {
			allowPan: true,
			allowZoom: true
		};
		this.tools.activeShape = '';
		this.tools.hasAOI = true;

		if (options.forceDraw) {
			//only draw in locate area;
			let taxLotExtent = geometry.extent;
			this.mapView.goTo({
				target: taxLotExtent
			});
		}
	}

	private async addUtilityGraphic(geometry: any): Promise<any> {
		const [Graphic] = await loadModules(['esri/Graphic']);

		let symbol;
		if (geometry.type === 'point' || geometry.type === 'multipoint') {
			symbol = this.mapService.getSymbol('marker');
		} else if (geometry.type === 'line' || geometry.type === 'polyline') {
			symbol = this.mapService.getSymbol('line');
		} else {
			symbol = this.mapService.getSymbol('fill');
		}

		let utilityLayer = await this.getMapLayer("utility");
		utilityLayer.removeAll();

		let newGraphic = new Graphic({
			geometry: geometry,
			symbol: symbol
		});

		utilityLayer.add(newGraphic);

		if (this.mode === "distance") {
			this.mapService.getLength(geometry).then(
				(length: any) => {
					let message = length.toFixed(2) + " " + this.mapControl.lengthUnit;
					this.alertsService.info({
						title: "Distance",
						message: message,
						delay: 5000
					});
					this.clearMapGraphics();
				},
				(err: any) => {
					console.error("Failed to get distance: ", err);
				}
			);
		} else if (this.mode === "area") {
			this.mapService.getArea(geometry).then(
				(areas: any) => {
					let message =
						"Geodesic: " + areas.geodesicArea.toFixed(2) + " " + this.mapControl.arealUnit + "<br> " +
						"Planar: " + areas.planarArea.toFixed(2) + " " + this.mapControl.arealUnit;
					this.alertsService.info({
						title: "Area",
						message: message,
						delay: 5000
					});
					this.clearMapGraphics();
				},
				(err: any) => {
					console.error("Failed to get area: ", err);
				}
			);
		}
	}

	async getMapLayer(id: string): Promise<any> {
		const [GraphicsLayer, MediaLayer] = await loadModules(['esri/layers/GraphicsLayer', 'esri/layers/MediaLayer']);

		if (!this.mapView) {
			Promise.reject("Map is not ready.");
		}

		let layer = this.mapView.map.findLayerById(id);
		if (layer) return layer;

		switch (id) {
			case "utility":
				// Can then find the layer by id --> let utilityLayer = this.mapView.map.findLayerById("utility");
				layer = new GraphicsLayer({
					id: "utility"
				});
				break;

			case "projection":
				layer = new MediaLayer({
					id: "projection",
					source: [],
					opacity: 0.9,
					blendMode: "normal"
				});
				break;

			case "aoi":
				layer = new GraphicsLayer({
					id: "aoi"
				});
				break;

			case "preview":
				layer = new GraphicsLayer({
					id: "preview"
				});
				break;

			case "location":
				layer = new GraphicsLayer({
					id: "location"
				});
				break;

			case "hash":
				layer = new GraphicsLayer({
					id: "hash"
				});
				break;

			case "multigeometry":
				layer = new GraphicsLayer({
					id: "multigeometry"
				});
				break;

			case "savedLocation":
				layer = new GraphicsLayer({
					id: "savedLocation"
				});
				break;

			case "wfs":
				layer = new GraphicsLayer({
					id: "wfs"
				});
				break;

			case "search":
				layer = new GraphicsLayer({
					id: "search"
				});
				break;
		}

		if (layer) {
			this.mapView.map.add(layer);
			console.log("Created new layer with id '%s'", layer.id);
		}

		return layer;
	}

	/**
	 * Add the user search results to the map WFS (Web Feature Service) layer as points and clusters.
	 */
	private async updateWFSLayer(): Promise<void> {
		let resultPoints: any[] = [];

		if (this.searchService.results) {
			let wfsLayer = await this.getMapLayer("wfs");
			if (wfsLayer.graphics.length > 0) {
				console.log("Removing %s layer graphics", wfsLayer.id);
				wfsLayer.removeAll();
			}

			const [
				Graphic,
				Color,
				Point,
				Polygon,
				webMercatorUtils,
				PictureMarkerSymbol
			] = await loadModules([
				'esri/Graphic',
				'esri/Color',
				'esri/geometry/Point',
				'esri/geometry/Polygon',
				'esri/geometry/support/webMercatorUtils',
				'esri/symbols/PictureMarkerSymbol'
			]);

			let mp = webMercatorUtils.webMercatorToGeographic(this.mapView.extent);
			let bbox = null;
			if (mp.xmin > mp.xmax) {
				bbox = -180 + "," + mp.ymin + "," + 180 + "," + mp.ymax;
			} else {
				bbox = mp.xmin + "," + mp.ymin + "," + mp.xmax + "," + mp.ymax;
			}
			console.log("Updating the WFS-service layer with search results using bbox %s", bbox);

			try {
				const data = await this.searchService.getWFS(bbox);
				let projectedJSON = this.mapService.getJSONfromXML(data);
				let wfsSelected = null;
				let selectedGraphic = null;

				if (projectedJSON != null) {
					if (projectedJSON["wfs:FeatureCollection"]) {
						let nodes = projectedJSON["wfs:FeatureCollection"]["gml:featureMember"];
						if (!Array.isArray(nodes)) {
							if (nodes == null || nodes["pwfs:aw_cluster"] == null) {
								nodes = [];
							} else {
								nodes = [nodes];
							}
						}
						for (let i = 0; i < nodes.length; i++) {
							let currNode = nodes[i];
							let aw_cluster = currNode["pwfs:aw_cluster"];
							let coordinates = aw_cluster["pwfs:the_geom"];
							if (coordinates["gml:Point"]) {
								coordinates = coordinates["gml:Point"]["gml:coordinates"];
								let pointCoordinates = coordinates.split(/[ ,]+/);
								coordinates = {
									point: [pointCoordinates[0], pointCoordinates[1]]
								};
								console.log("Point coordinates: %s", JSON.stringify(coordinates));
							}
							else if (!coordinates["gml:MultiLineString"] && coordinates["gml:MultiPoint"]) {
								coordinates = coordinates["gml:MultiPoint"]["gml:pointMember"]["gml:Point"]["gml:coordinates"];
								let pointCoordinates = coordinates.split(/[ ,]+/);
								coordinates = {
									point: [pointCoordinates[0], pointCoordinates[1]]
								};
								console.log("MultiPoint coordinates: %s", JSON.stringify(coordinates));
							}
							else {
								let gip = coordinates["gml:MultiLineString"]["gml:lineStringMember"]["gml:coordinates"];
								if (!Array.isArray(coordinates["gml:MultiLineString"]["gml:lineStringMember"])) {
									coordinates["gml:MultiLineString"]["gml:lineStringMember"] = [coordinates["gml:MultiLineString"]["gml:lineStringMember"]];
								}
								let lineMembers = coordinates["gml:MultiLineString"]["gml:lineStringMember"];
								coordinates.polygon = [];
								for (let li = 0; li < lineMembers.length; li++) {
									let lineString = lineMembers[li]["gml:LineString"]["gml:coordinates"];
									let member = lineString.split(/[ ,]+/);
									let polyRing = [];
									for (let x = 0; x < member.length; x += 2) {
										polyRing.push([member[x + 0], member[x + 1]]);
									}
									coordinates.polygon.push(polyRing);
								}
								console.log("Polygon coordinates: %s", coordinates);
							}

							let numItems = aw_cluster["pwfs:numItems"] ? aw_cluster["pwfs:numItems"] : 1;
							let geoHash = aw_cluster["pwfs:geoHash"];
							let uuid = aw_cluster["pwfs:uuid"];
							let title = aw_cluster["pwfs:title"];
							let symbol_code = aw_cluster["pwfs:common.symbol_code"];
							let library = aw_cluster["pwfs:library"];
							let polyWFS: any = null;
							let styleColor = aw_cluster["pwfs:style_color"];

							if (coordinates.polygon && coordinates.polygon.length > 0) {
								let poly = new Polygon();
								for (let p = 0; p < coordinates.polygon.length; p++) {
									poly.addRing(coordinates.polygon[p]);
								}
								poly = webMercatorUtils.geographicToWebMercator(poly);
								let d = poly.getCentroid();
								d = webMercatorUtils.webMercatorToGeographic(d);

								coordinates = {
									point: [d.x, d.y]
								};

								polyWFS = new Graphic(poly, this.mapService.getSymbol('poly-wfs'), {
									isPolyWFS: true,
									uuid: uuid
								});

								this.getMapLayer("wfs").then((layer: any) => {
									layer.add(polyWFS);
								});
							}

							let geom = new Point(coordinates.point[0], coordinates.point[1]);
							geom = webMercatorUtils.geographicToWebMercator(geom);
							let pointAttributes = {
								geoHash: geoHash,
								count: numItems,
								uuid: uuid,
								polyWFS: polyWFS,
								symbol_code: symbol_code,
								coordinates: `${coordinates.point[0]},${coordinates.point[1]}`,
								selected: false,
								isCluster: false
							};

							let selected = true;

							pointAttributes.selected = selected;

							if (numItems > 1) {
								pointAttributes.isCluster = true;

								let layer = await this.getMapLayer("wfs");
								layer.add(new Graphic(geom, selected ? this.mapService.getSymbol('cluster-selected', numItems) : this.mapService.getSymbol('cluster', numItems), pointAttributes, null));
								layer.add(new Graphic(geom, this.mapService.getSymbol('text', undefined, numItems), {
									geoHash: geoHash,
									isText: true
								}, null));

								resultPoints.push({
									'is_cluster': true,
									'geoHash': pointAttributes.geoHash,
									'count': numItems,
									'title': `Cluster of ${numItems} results`,
									'coordinates': `${coordinates.point[0]},${coordinates.point[1]}`
								});

								console.log("Cluster marker of %s items", numItems);

							}
							else {
								let symbol = selected ? this.mapService.getSymbol('single-selected') : this.mapService.getSymbol('single');
								if (pointAttributes.symbol_code) {
									// Best guess is that 'ms' is the military symbology library, milsymbol.js
									// let msSymbol = new ms.Symbol(pointAttributes.symbol_code, { size: 30, square: true });
									// symbol = new PictureMarkerSymbol(msSymbol.toDataURL(), 30, 30);
								} else {
									if (!selected && styleColor) {
										if (styleColor.indexOf("#") == 0) {
											symbol = this.mapService.getSymbol('single');
											symbol.color = new Color(styleColor);
										} else {
											let colorsRGB = styleColor.split(",");
											if (colorsRGB.length == 3) {
												symbol = this.mapService.getSymbol('single');
												symbol.color = [colorsRGB[0], colorsRGB[1], colorsRGB[2], 1];
											}
										}
									}
								}

								// NOTE: map may not reflect graphics change when open in debugger
								let currGraphic = new Graphic(geom, symbol, pointAttributes);
								this.getMapLayer("wfs").then((layer: any) => {
									layer.add(currGraphic);
								});

								if (selected) {
									selectedGraphic = currGraphic;
									if (currGraphic.attributes.polyWFS) {
										currGraphic.attributes.polyWFS.setSymbol(this.mapService.getSymbol('poly-wfs-selected'));
									}
								}

								resultPoints.push({
									'uuid': uuid,
									'title': title,
									'library': library,
									'count': numItems,
									'product_size': aw_cluster["pwfs:file_size"],
									'file_extension': aw_cluster["pwfs:file_extension"],
									'coordinates': `${coordinates.point[0]},${coordinates.point[1]}`
								});

								console.log("Single marker for id %s", uuid);
							}
						}
					}
				}

				this.updateFilmstrip(resultPoints);

				if (selectedGraphic && !selectedGraphic.attributes.isCluster) {
					let layer = await this.getMapLayer("wfs");
					layer.remove(selectedGraphic);
					layer.add(selectedGraphic);
					let polyWFS = selectedGraphic.attributes.polyWFS;
					if (polyWFS) {
						layer.remove(polyWFS);
						layer.add(polyWFS);
					}
					// $timeout(function () {
					//	 this.scroller.mCustomScrollbar('scrollTo', '#record_' + selectedGraphic.attributes.uuid, { scrollEasing: "easeInOut" });
					// }, 100);
				}

				this.getMapLayer("hash").then((layer: any) => {
					layer.removeAll();
				});

			} catch (err) {
				console.error("Error getting search results for bounding box: ", err);
			}
		} else {
			let wfsLayer = await this.getMapLayer("wfs");
			if (wfsLayer.graphics.length > 0) {
				wfsLayer.removeAll();

				this.getMapLayer("preview").then((layer: any) => {
					layer.removeAll();
				});
				this.getMapLayer("multigeometry").then((layer: any) => {
					layer.removeAll();
				});
				this.getMapLayer("projection").then((layer: any) => {
					layer.removeAll();
				});
			}
		}
	}

	/**
	 * Process the WFS (Web Feature Service) layer graphics.
	 */
	private async processWFSGraphics(extentGeom?: any): Promise<void> {
		this.getMapLayer("preview").then((layer: any) => {
			layer.removeAll();
		});

		this.getMapLayer("multigeometry").then((layer: any) => {
			layer.removeAll();
		});

		const [
			//			Graphic,
			Color
			//			Point,
			//			Polygon,
			//			webMercatorUtils,
			//			PictureMarkerSymbol
		] = await loadModules([
			//			'esri/Graphic',
			'esri/Color'
			//			'esri/geometry/Point',
			//			'esri/geometry/Polygon',
			//			'esri/geometry/support/webMercatorUtils',
			//			'esri/symbols/PictureMarkerSymbol'
		]);

		let mainSelected: any = null;
		let polyWFS: any = null;

		let layer = await this.getMapLayer("wfs");
		let wfsGraphics = layer.graphics;
		wfsGraphics.forEach((currGraphic: any) => {
			if (currGraphic.attributes && !currGraphic.attributes.isPolyWFS) {
				let selected = currGraphic.attributes.selected;
				let isCluster = currGraphic.attributes.uuid == null ? true : false;
				let isText = currGraphic.attributes.isText != null ? true : false;
				if (extentGeom && extentGeom.normalize()[0].contains(currGraphic.geometry) && !isText && !selected && !mainSelected) {
					currGraphic.attributes.selected = true;
					let symbol = isCluster ? this.mapService.getSymbol('cluster', currGraphic.attributes.count) : this.mapService.getSymbol('single-selected');

					// If the graphic has a specific symbol code, override the default symbol assignment
					if (currGraphic.attributes.symbol_code) {
						// Best guess is that 'ms' is the military symbology library, milsymbol.js
						// let msSymbol = new ms.Symbol(currGraphic.attributes.symbol_code, { size: 30, square: true, outlineColor: "Red", outlineWidth: 3 });
						// symbol = new PictureMarkerSymbol(msSymbol.toDataURL(), 30, 30);
					}

					currGraphic.symbol = symbol;
					mainSelected = currGraphic;
					if (currGraphic.attributes.polyWFS) {
						polyWFS = currGraphic.attributes.polyWFS;
						currGraphic.attributes.polyWFS.setSymbol(this.mapService.getSymbol('poly-wfs-selected'));
					}

					let graphicCopy = cloneDeep(mainSelected.attributes);

					// Normalize due to Esri's circular reference
					graphicCopy.polyWFS = null;
					graphicCopy.original_symbol = null;

					this.onUpdateMapRecordPreview(graphicCopy);

					// // message_broadcast is defined in controllers.js SearchCtrl
					// this.message_broadcast({ 'action': 'updateMapRecordPreview', 'data': graphicCopy });
					// this.scroller.mCustomScrollbar('scrollTo', '#record_' + mainSelected.attributes.uuid, { scrollEasing: "easeInOut" });
				}
				else if (!isText) {
					currGraphic.attributes.selected = false;
					let symbol = isCluster ? this.mapService.getSymbol('cluster', currGraphic.attributes.count) : this.mapService.getSymbol('single');

					// If the graphic has a specific symbol code, override the default symbol assignment
					if (currGraphic.attributes.symbol_code) {
						// Best guess is that 'ms' is the military symbology library, milsymbol.js
						// let msSymbol = new ms.Symbol(currGraphic.attributes.symbol_code, { size: 30, square: true });
						// symbol = new PictureMarkerSymbol(msSymbol.toDataURL(), 30, 30);
					} else {
						if (currGraphic.attributes.styleColor) {
							if (currGraphic.attributes.styleColor.indexOf("#") == 0) {
								symbol = this.mapService.getSymbol('single');
								symbol.color = new Color(currGraphic.attributes.styleColor);
							} else {
								let colorsRGB = currGraphic.attributes.styleColor.split(",");
								if (colorsRGB.length == 3) {
									symbol = this.mapService.getSymbol('single');
									symbol.color = [colorsRGB[0], colorsRGB[1], colorsRGB[2], 1];
								}
							}
						}
					}
					currGraphic.symbol = symbol;
					if (currGraphic.attributes.polyWFS) {
						currGraphic.attributes.polyWFS.symbol = this.mapService.getSymbol('poly-wfs');
					}
				}
			}
		});

		if (mainSelected && !mainSelected.attributes.isCluster) {
			let layer = await this.getMapLayer("wfs");
			layer.remove(mainSelected);
			layer.add(mainSelected);
			if (polyWFS) {
				layer.remove(polyWFS);
				layer.add(polyWFS);
			}
		}

		this.getMapLayer("hash").then((layer: any) => {
			layer.removeAll();
		});
	}

	// TODO do we need this?  for tree layer data
	private processNode(data: any, check: boolean): void {
		// Check if top node is a hit
		let mapLayer = this.mapView.map.findLayerById(data.node.data.mapLayerID);
		if (!mapLayer) {
			return;
		}

		let layerID = data.node.data.layerID;
		if (layerID == -1) {
			mapLayer.visible = check;
			return;
		}

		let newVisibility: string[] = [];
		let parser = {};
		let allChecked = data.selected;
		for (let i = 0; i < allChecked.length; i++) {
			let node = allChecked[i];
			if (node.indexOf(mapLayer.id) == -1 || node == "tree_" + mapLayer.id) {
				continue;
			}
			//		             let node = this.vm.treeInstance.jstree(true).get_node("[id='" + node + "']");
			//		             parser[node.id] = 1;
			//		             let parents = node.parents;
			//		             for (let x = 0; x < parents.length; x++) {
			//		                 //if(parents[x] != "#" && parents[x] != "tree_"+mapLayer.id )
			//		                 //parser[parents[x]] = 1;
			//		             }
		}

		for (let layer in parser) {
			let id = layer.replace("tree_" + mapLayer.id + "_", "");
			newVisibility.push(id);
		}

		let layerVisibility = newVisibility;
		mapLayer.allSublayers.map((layer: any) => {
			if (newVisibility.includes(layer.id)) {
				layer.visible = true;
			} else {
				layer.visible = false;
			}
		});
	}

	//
	// Tree layers
	//
	deselectAllLayers(): void {
		// vm.treeInstance.jstree("uncheck_all");
		this.processNode([], true);
	}

	selectAllLayers(): void {
		//  vm.treeInstance.jstree("check_all");
		this.processNode([], false);
	}

	check_node(e: any, data: any): void {
		this.processNode(data, true);
	}

	uncheck_node(e: any, data: any): void {
		this.processNode(data, false);
	}

	/** 
	 * Tracks additional map layers in a set the user can turn on and off.
	 */
	private async processLayers(): Promise<void> {
		//	     let vm = {};
		//	     this.vm = vm;
		//	     vm.treeConfig = {
		//	         core: {
		//	             multiple: true,
		//	             animation: true,
		//	             error: function (error) {
		//	                 $log.error('treeCtrl: error from js tree - ' + angular.toJson(error))
		//	             },
		//	             check_callback: true,
		//	             worker: true
		//	         },
		//	         types: {
		//	             default: {
		//	                 //icon : 'glyphicon glyphicon-flash'
		//	             },
		//	             "layer": {
		//	                 icon: 'glyphicon glyphicon-pushpin'
		//	             }
		//	         },
		//	         checkbox: {
		//	             three_state: false,
		//	             tie_selection: false
		//	         },
		//	         version: 1,
		//	         plugins: ['types', 'checkbox', 'noclose']
		//	     };
		//
		//	     vm.treeData = [];
		//	     vm.readyCB = function () {
		//	     };

		this.treeData = [];
		let layers = [];
		if (!this.inputLayers) { return; }

		const [WFSLayer, MapImageLayer, FeatureLayer] =
			await loadModules([
				'esri/layers/WFSLayer',
				'esri/layers/MapImageLayer',
				'esri/layers/FeatureLayer'
			]);

		this.inputLayers.forEach((value: any) => {
			let popupTemplate = {
				title: "&nbsp;",
				content: (feature: any) => {
					let content = "<table class='table table-bordered'><tbody>";
					for (const [key, value] of Object.entries(feature.graphic.attributes)) {
						content += `<tr><td>${key}</td><td>${value}</td></tr>`;
					}
					content += "</tbody></table>";
					return content;
				}
			}

			if (value.infoTemplate) {
				try {
					let template = JSON.parse(value.infoTemplate);
					if (template.title) { popupTemplate.title = template.title };
					if (template.content) { popupTemplate.content = template.content };
				} catch (err) {
					console.error("Failed to parse the template: ", err);
				}
			}

			if (value.type === "WFS") {
				let wfsLayer = new WFSLayer({
					url: value.url,
					visible: value.visible,
					id: value.name,
					popupTemplate: popupTemplate
				});
				wfsLayer.when(
					(data: any) => {
						let ID = wfsLayer.id;

						this.treeData.push({
							id: "tree_" + ID,
							parent: "#",
							data: {
								layerID: -1,
								mapLayerID: ID
							},
							text: ID,
							//							state: {
							checked: wfsLayer.visible,
							opened: true
							//							}
						});
					},
					(err: any) => {
						console.error("Failed to process layer: ", err);
					}
				);
				this.mapView.map.add(wfsLayer);
			}
			else if (value.type === "MAPSERVER") {
				let dynamicLayer = new MapImageLayer(value.url, {
					visible: value.visible ? true : false,
					id: value.name
				});
				dynamicLayer.when(
					(data: any) => {
						let ID = dynamicLayer.id;

						this.treeData.push({
							id: "tree_" + ID,
							parent: "#",
							data: {
								layerID: -1,
								mapLayerID: ID
							},
							text: ID,
							state: {
								checked: true,
								opened: true
							}
						});

						dynamicLayer.allSublayers.map((layer: any) => {
							layer.popupTemplate = popupTemplate;
							let parentId = layer.parent.id == ID ? "tree_" + layer.parent.id : "tree_" + ID + "_" + layer.parent.id;
							let id = "tree_" + ID + "_" + layer.id;
							let type = layer.sublayers ? null : "layer";

							this.treeData.push({
								data: {
									layerID: layer.id,
									mapLayerID: ID
								},
								type: type,
								id: id,
								parent: parentId,
								text: layer.title,
								a_attr: {
									"class": type == "layer" ? null : "no_checkbox"
								},
								//								state: {
								checked: layer.visible && type == "layer",
								opened: true
								//								}
							});
						});
						//						this.vm.treeConfig.version++;
					},
					(err: any) => {
						console.error("Failed to process layer: ", err);
					}
				);
				this.mapView.map.add(dynamicLayer);
			}
			else if (value.type === "FEATURESERVER") {
				let featureLayer = new FeatureLayer({
					url: value.url,
					outFields: ["*"],
					visible: value.visible ? true : false,
					id: value.name,
					popupTemplate: popupTemplate
				});
				featureLayer.when(
					(data: any) => {
						let ID = featureLayer.id;
						this.treeData.push({
							id: "tree_" + ID,
							parent: "#",
							data: {
								layerID: -1,
								mapLayerID: ID,
								featureLayer: true
							},
							text: ID,
							//							state: {
							checked: true,
							opened: true
							//							}
						});
						//						this.vm.treeConfig.version++;
					},
					(err: any) => {
						console.error("Failed to process layer: ", err);
					}
				);
				this.mapView.map.add(featureLayer);
			}
		});
	}

	changeBasemap(): void {
		this.mapService.loadBaseMap(this.mapView, this.mapControl.baseMapUnit);
	}

	changingSettings(): void {
		this.mapService.updateMapControlSettings(this.mapControl);
	}

	updateGeometrySearchPredicate(): void {
		this.mapService.broadcastUpdateGeometrySearchPredicateEvent({ value: this.mapControl.geometrySearchPredicate });
	}

	fullExtent(): void {
		let coords = this.configService.getConfig().esri.center ? this.configService.getConfig().esri.center.split(",") : ["0", "0"];
		this.mapService.centerAndZoomMap(this.mapView, coords, this.configService.getConfig().esri.zoom);
	}

	private unselectRecordMap() {
		this.previewOpen = false;
		this.mapView.popup.visible = false;
		this.processWFSGraphics(null);
		if (this.mapDetached) {
			//	        this.message_broadcast({ 'action': 'updateMapRecordPreview', 'data': null });
		}
	}

	// Called from document popup "Display Points".
	// Only used if the document has 'pages' metadata field.
	private async onExpandRecord(record: any): Promise<void> {
		const [
			Graphic,
			Point,
			webMercatorUtils
		] =
			await loadModules([
				'esri/Graphic',
				'esri/geometry/Point',
				'esri/geometry/support/webMercatorUtils'
			]);

		let layer = await this.getMapLayer("multigeometry");
		layer.removeAll();

		let response = await this.mapService.recordPageGeometries(record.id);

		response.forEach((point: any) => {
			if (point.geo) {
				let beg = point.geo.indexOf("(");
				let end = point.geo.indexOf(")");
				let sub = point.geo.substring(beg + 1, end);
				let comp = sub.split(/[\s,]+/);
				let y = comp[1];
				let x = comp[0];
				let geom = new Point([x * 1, y * 1]);
				geom = webMercatorUtils.geographicToWebMercator(geom);
				let _thumb = formatString(this.configService.getConfig().document.thumbnailPageURL, record.id, point.page, 0, "STANDARD");
				let pointAttributes = {
					page: point.page,
					name: point.name,
					_thumb: _thumb,
					record: record.id,
					projected: record.projectedPages.includes(point.page)
				};
				let pointGraphic = new Graphic(geom, this.mapService.getSymbol('sub-single'), pointAttributes);
				layer.add(pointGraphic);

				//     let textSymbol = new TextSymbol(point.page).setColor(
				//     new Color([255, 255, 255])).setAlign(Font.ALIGN_START).setAngle(0).setOffset(0, -5).setFont(
				//     new Font("10pt").setWeight(Font.WEIGHT_BOLD));
				//     layer.add(new Graphic(geom, textSymbol, {}, null));
			}
		});

		if (layer.graphics && layer.graphics.length > 0) {
			layer.graphics = layer.graphics.sort((a: any, b: any) => {
				if (a.attributes.page > b.attributes.page) {
					return 1;
				} else if (b.attributes.page > a.attributes.page) {
					return -1;
				} else {
					return 0;
				}
			});
			let currentMultiGeometry = 0;
			layer.graphics.items[currentMultiGeometry].symbol = this.mapService.getSymbol('sub-single-selected');
		}
	}

	updateFilmstrip(results: any[]): void {
		this.filmstripRecords = [];
		if (results) {
			let totalGeoCount = 0;
			for (let i = 0; i < results.length; i++) {
				let record = results[i];
				totalGeoCount += +record.count;
				if (!record.title && record.count > 1) {
					record.title = "Cluster of " + record.count + " results";
				}

				let target = {};
				let source = record;
				let filmstripRecord = Object.assign(target, source);
				filmstripRecord._thumbs = !record.is_cluster ? formatString(this.configService.getConfig().record.thumbnailURL, record.uuid) : undefined;
				filmstripRecord.tooltip = record.title && record.library ? (record.title + " - " + record.library) : record.title;

				this.filmstripRecords.push(filmstripRecord);
			}
			if (this.searchService.results) {
				this.searchService.results.geo_total = totalGeoCount;
			}
		}

		this.mapService.broadcastFilmstripResultEvent({ filmstripRecords: this.filmstripRecords });
	}

	async onZoomTo(record: any): Promise<void> {
		let geom = await this.geoService.parseWKT(record.geo);
		if (!geom) { return; }

		let taxLotExtent = geom.extent;
		if (taxLotExtent) {
			this.mapView.goTo({
				target: taxLotExtent
			});
		} else {
			// Else we can assume it's a point
			this.mapService.centerAndZoomMap(this.mapView, [geom.x, geom.y], 18);
		}
	}

	onUpdateMapRecordPreview(record: any): void {
		this.previewOpen = true;
		this.processWFSGraphics(null);

		//		let data = {
		//			'uuid': record.uuid
		//		};
		//       $scope.sharedData.selectedMapRecord = data;
		//	    this.message_broadcast({ 'action': 'updateMapRecordPreview', 'data': data });

		this.mapService.broadcastUpdatePreviewEvent({
			uuid: record.uuid,
			geoHash: record.geoHash,
			forceZoom: false
		});

		this.searchService.broadcastSwitchViewerModeEvent({ mode: 'Results' });
	}

	async onUpdateMapRecordActiveLocation(record: any, isactive: boolean = false): Promise<void> {
		console.log('updateMapRecordActiveLocation (active=%s) for:', isactive, record);
		let coords = record.coordinates;

		let layer = await this.getMapLayer("wfs");
		let graphics = layer.graphics.items;
		let graphicToUpdate = layer.graphics.find((graphic: any) =>
			graphic.attributes.coordinates === coords
		);
		if (graphicToUpdate) {
			// preserve the original colored symbol
			if (!graphicToUpdate.attributes.original_symbol) {
				let symbol = graphicToUpdate.symbol.clone();
				graphicToUpdate.attributes.original_symbol = symbol;
			}

			if (isactive) {
				console.log("Active graphic = uuid: %s, title: %s", graphicToUpdate.attributes.uuid, record.title);

				let symbol = graphicToUpdate.symbol.clone();
				symbol.color = [255, 0, 0, 0.8]; // red with 80% opacity
				graphicToUpdate.symbol = symbol;
			} else {
				console.log("Unsetting active graphic = uuid: %s, title: %s", graphicToUpdate.attributes.uuid, record.title);

				let originalSymbol = graphicToUpdate.attributes.original_symbol;
				let clonedSymbol = originalSymbol.clone();
				graphicToUpdate.symbol = clonedSymbol;
			}
			this.mapView.requestUpdate();
		}
	}

	toggleMode(mode: string): void {
		if (this.mode === mode) {
			// If unselecting a specific mode, default back to the pan mode
			mode = "pan";
		}
		this.mode = mode;
		this.clearMapGraphics();
		this.unselectRecordMap();

		this.getMapLayer("location").then((layer: any) => {
			layer.removeAll();
		});

		switch (mode) {
			case "zoomin":
				this.mapView.goTo({
					center: this.mapView.center,
					zoom: this.mapView.zoom + 1
				});
				break;

			case "zoomout":
				this.mapView.goTo({
					center: this.mapView.center,
					zoom: this.mapView.zoom - 1
				});
				break;

			case "distance":
				this.activateDrawTool("polyline");
				break;

			case "area":
				this.activateDrawTool("polygon");
				break;

			case "pan":
				this.sketchViewModel.cancel();  // Cancel any drawing or tools
				this.mapView.navigationConstraints = {
					allowPan: true,
					allowZoom: true
				};
				break;
		}
	}

	toggleOverview(show: boolean): void {
		console.log("Unsupported");
		// NOTE Overview map functionality is currently not well supported
		// For examples, see:  https://developers.arcgis.com/javascript/latest/sample-code/sandbox/?sample=overview-map
	}

	wktToAoi(wkt: string, forceZoom: boolean = false) {
		this.mapService.goToWKTLocation(wkt, forceZoom);
	}

	async centerAOI(): Promise<void> {
		let layer = await this.getMapLayer("aoi");
		let graphic = layer.graphics.getItemAt(0);
		let taxLotExtent = graphic.geometry.extent;
		if (taxLotExtent) {
			this.mapView.goTo({
				target: taxLotExtent
			});
		} else {
			this.mapView.goTo({
				target: graphic.geometry,
				zoom: 18
			});
		}
	}

	addAOI(geometry: any, options: any): void {
		this.addGraphic(geometry, options);

		if (options.forceZoom) {
			this.mapView.goTo({
				target: geometry.extent
			});
		}
	}

	clearAOI(): void {
		this.getMapLayer("aoi").then((layer: any) => {
			layer.removeAll();
		});
		this.getMapLayer("utility").then((layer: any) => {
			layer.removeAll();
		});
		this.mapService.broadcastUpdateAOIEvent({ aoi: null });
	}

	private async onAoiOnOff(isOn: boolean): Promise<void> {
		let layer = await this.getMapLayer("aoi");

		if (!layer || !layer.graphics || layer.graphics.length == 0) {
			return;
		}

		let graphic = layer.graphics.getItemAt(0);
		if (graphic.geometry.type === "point") {
			graphic.symbol = this.mapService.getSymbol('locate-point');
		} else {
			graphic.symbol = isOn ? this.mapService.getSymbol('aoi-fill') : this.mapService.getSymbol('aoi');
		}
	}

	aoiOn(): void {
		this.onAoiOnOff(true);
	}

	aoiOff(): void {
		this.onAoiOnOff(false);
	}

	async saveAOI(): Promise<void> {
		let wktString = "";
		let layer = await this.getMapLayer("aoi");
		if (layer.graphics && layer.graphics.length > 0 && layer.graphics.getItemAt(0).attributes) {
			wktString = layer.graphics.getItemAt(0).attributes["wkt"];
		} else {
			return;
		}

		const modalRef = this.ngbModal.open(SaveLocationComponent, {});

		modalRef.componentInstance.locationData = {
			location: null,
			geo: wktString
		};

		modalRef.result.then(
			(payload) => {
				if (payload?.answer === 'ok') {
					if (payload.location.name) {
						this.addNewSavedLocation(payload.location);
					}
				}
			},
			(reason) => {
				console.log('Modal dismissed');
			}
		);
	}

	private async addNewSavedLocation(locationData: any): Promise<void> {
		const [Graphic] = await loadModules(['esri/Graphic']);
		let layer = await this.getMapLayer("aoi");
		let graphic = layer.graphics.getItemAt(0);

		let location = new Graphic({
			geometry: graphic.geometry,
			symbol: graphic.geometry.type == "point" ?
				this.mapService.getSymbol('locate-point') :
				this.mapService.getSymbol('locate-fill')
		});
		location.attributes = {};
		location.attributes["id"] = locationData.id;
		location.attributes["name"] = locationData.name;
		location.attributes["geo"] = locationData.geo;

		this.getMapLayer("savedLocation").then((layer: any) => {
			layer.add(location);
		});
	}

	async searchAOI(): Promise<void> {
		let wktString = "";
		let layer = await this.getMapLayer("aoi");
		if (layer.graphics && layer.graphics.length > 0) {
			wktString = layer.graphics.getItemAt(0).attributes["wkt"];
			this.mapService.broadcastUpdateAOIEvent({
				aoi: wktString
			});
		}
	}

	activeShape(shape: string): void {
		this.sketchViewModel.cancel();
		this.mapView.navigationConstraints = {
			allowPan: true,
			allowZoom: true
		};

		let prevShape = this.tools.activeShape;
		this.toggleMode("pan");
		this.tools.activeShape = prevShape;

		if (shape === this.tools.activeShape) {
			this.tools.activeShape = '';
			this.clearMapGraphics();
		} else {
			this.clearMapGraphics();
			this.tools.activeShape = shape;
			if (shape !== "extent") {
				this.activateDrawTool(shape);
			} else {
				this.searchByExtent();
			}
		}
	}

	activateDrawTool(tool: string): void {
		this.mapView.navigationConstraints = {
			allowPan: false,
			allowZoom: false
		};
		this.sketchViewModel.create(tool.toLowerCase());
	}

	clearMapGraphics(): void {
		this.getMapLayer("aoi").then((layer: any) => {
			layer.removeAll();
		});

		this.getMapLayer("utility").then((layer: any) => {
			layer.removeAll();
		});

		this.getMapLayer("savedLocation").then((layer: any) => {
			layer.removeAll();
		});

		this.getMapLayer("projection").then((layer: any) => {
			layer.source.elements.removeAll();
		});

		this.tools.activeShape = '';
		this.tools.hasAOI = false;
	}

	async searchByExtent(): Promise<void> {
		const [Polygon, webMercatorUtils] =
			await loadModules([
				'esri/geometry/Polygon',
				'esri/geometry/support/webMercatorUtils'
			]);

		let layer = await this.getMapLayer("aoi");
		layer.removeAll();

		let extent = webMercatorUtils.webMercatorToGeographic(this.mapView.extent);
		let polygon = new Polygon({
			spatialReference: extent.spatialReference,
			// Add a single ring
			rings: [[
				[extent.xmin, extent.ymax],
				[extent.xmax, extent.ymax],
				[extent.xmax, extent.ymin],
				[extent.xmin, extent.ymin],
				[extent.xmin, extent.ymax]
			]]
		});
		let geojson = Terraformer.ArcGIS.parse(polygon);
		let wktString = Terraformer.WKT.convert(geojson);

		this.mapService.broadcastUpdateAOIEvent({ aoi: wktString });
	}

	toggleChip(open: boolean): void {
		this.chipOn = open;
		if (this.chipOn) {
			this.supportedPackageTypes.length = 0;
			this.supportedPackageTypes.push({
				display: this.LOCALIZATION.defaultVal,
				value: "PRODUCT"
			});
			this.supportedPackageTypes.push({
				display: 'Word',
				value: "WORD"
			});
			this.supportedPackageTypes.push({
				display: 'Powerpoint',
				value: "PPT"
			});
			this.supportedPackageTypes.push({
				display: 'Animated GIF',
				value: "GIF"
			});
		} else {
			this.clearChipDraw();
		}
	}

	enableChipDraw(): void {
		this.activateDrawTool('rectangle');
	}

	async clearChipDraw(): Promise<void> {
		this.toggleMode("pan");

		let layer = await this.getMapLayer("utility");
		layer.removeAll();
		// Refresh the layer to update the map
		layer.emit("graphics-update");

		// TODO
		//	    this.chip.searchTotal = 0;
		//	    this.chip.searchResults = [];
		//	    this.chip.searching = false;
		//	    this.chip.x1 = 0;
		//	    this.chip.x2 = 0;
		//	    this.chip.y1 = 0;
		//	    this.chip.y2 = 0;
		//	    this.chip.wkt = "";
		//	    this.supportedChipFormats = [];
	}

	searchChipProducts(pageIndex?: number, pageSize?: number, wktString?: string): void {
		//	    if (pageIndex) {
		//	        this.chip.pageIndex = pageIndex;
		//	    }
		//	    if (pageSize) {
		//	        this.chip.pageSize = pageSize;
		//	    }
		//
		//	    let url = configService.getConfig().search.searchURL;
		//	    url += "CONSTRAINT=" + searchService.buildQuery("*", wktString, "product_type=[TIFF OR NITF]", '16', null) + "&" + decodeURIComponent($.param(configService.getConfig().search.searchParms)) + "&STARTPOSITION=" + ((this.chip.pageIndex - 1) * this.chip.pageSize + 1) + "&MAXRECORDS=" + this.chip.pageSize;
		//
		//	    this.chip.searching = true;
		//	    $http.get(url).then(function (response) {
		//	        let results = response.data["csw:GetRecordsResponse"]["csw:SearchResults"];
		//	        if (results) {
		//	            this.chip.searching = false;
		//	            this.chip.searchTotal = results.numberOfRecordsMatched;
		//	            this.chip.searchResults = results.iStoreRecord == null ? [] : Array.isArray(results.iStoreRecord) ? results.iStoreRecord : [results.iStoreRecord];
		//	            for (let i = 0; i < this.chip.searchResults.length; i++) {
		//	                let rec = this.chip.searchResults[i];
		//	                rec._thumbs = configService.getConfig().record.thumbnailURL.format(rec.id);
		//	            }
		//	        } else {
		//	            this.chip.searching = false;
		//	            this.chip.searchResults = [];
		//	            this.chip.searchTotal = 0;
		//	        }
		//	    }, function () {
		//	        this.chip.searching = false;
		//	        this.chip.searchResults = [];
		//	        this.chip.searchTotal = 0
		//	    });
	}














	@HostListener('window:resize', ['$event'])
	onResize(event: any): void {
		setTimeout(() => {
			this.handleOverflow();
		}, 100);
	}

	handleOverflow() {
		const width = window.innerWidth;
		const offset = 160; // Toolbar dropdown offset

		if (width < 770 + offset) {
			this.hide = { hideTools: true, hideExtent: true, hideDetach: true };
		} else if (width < 825 + offset) {
			this.hide = { hideTools: true, hideExtent: true, hideDetach: false };
		} else if (width < 920 + offset) {
			this.hide = { hideTools: true, hideExtent: true, hideDetach: false };
		} else if (width < 1190 + offset) {
			this.hide = { hideTools: true, hideExtent: false, hideDetach: false };
		} else {
			this.hide = { hideTools: false, hideExtent: false, hideDetach: false };
		}
	}

	detachMap() {
		setTimeout(() => this.openDetachedWindow(), 1);
	}

	private openDetachedWindow() {
		// Get current route parameters
		const params = { ...this.routingService.getQueryParams(), v: 'M', mapDetach: true };

		// Generate new URL
		const urlTree = this.router.createUrlTree(['/s'], { queryParams: params });
		const sanitizedUrl = this.sanitizer.sanitize(1, window.location.origin + this.router.serializeUrl(urlTree));

		if (sanitizedUrl) {
			const winObj = window;
			let childWindow = window.open(
				sanitizedUrl,
				'',
				`height=${winObj.innerHeight},width=${winObj.innerWidth},resizable=yes,scrollbars=yes,toolbar=yes,menubar=no,location=no,directories=no,status=yes`
			);
			
			// TODO
			//			this.changeView('G');
			//			this.mapDetached = true;
			//			this.searchService.mapDetached = true; //to assist in running a new search across the tabs;
			//			this.searchService.broadcastEvent.emit({ type: 'mapDetached', data: {} });			
		}
	}
}
